import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import {
  Activity, Clock, CheckCircle, XCircle, Calendar, Filter, RefreshCw, 
  Zap, Shield, BarChart3, Search, Download, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, FileText, Key, Globe, Eye, X, AlertCircle
} from 'lucide-react';
import { usageService, apiKeyService } from '../utils/api';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';

const AnalyticsLogsPage = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('analytics');
  
  // Analytics state
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [period, setPeriod] = useState('7d');
  const [selectedKey, setSelectedKey] = useState('');
  const [apiKeys, setApiKeys] = useState([]);
  const [stats, setStats] = useState(null);
  const [overview, setOverview] = useState(null);

  // Logs state
  const [logsLoading, setLogsLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    keyId: '',
    status: '',
    endpoint: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const [refreshing, setRefreshing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const mountedRef = useRef(true);

  const periods = [
    { value: '24h', label: '24H' },
    { value: '7d', label: '7D' },
    { value: '30d', label: '30D' },
    { value: '90d', label: '90D' }
  ];

  const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6'];

  const endpoints = [
    { value: '', label: 'All Endpoints' },
    { value: '/api/analyze/single', label: 'Single Analysis' },
    { value: '/api/analyze/multi', label: 'Multi Analysis' },
    { value: '/api/analyze/urls', label: 'URL Analysis' },
    { value: '/api/analyze/streaming', label: 'Streaming' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: '200', label: '200 - Success' },
    { value: '400', label: '400 - Bad Request' },
    { value: '401', label: '401 - Unauthorized' },
    { value: '429', label: '429 - Rate Limited' },
    { value: '500', label: '500 - Server Error' }
  ];

  // Initial mount - wait a moment for auth to be ready
  useEffect(() => {
    mountedRef.current = true;
    const timer = setTimeout(() => {
      if (mountedRef.current) {
        setIsReady(true);
      }
    }, 300);
    
    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (isReady) {
      fetchApiKeys();
    }
  }, [isReady]);

  useEffect(() => {
    if (isReady && activeTab === 'analytics') {
      fetchAnalytics();
    }
  }, [period, selectedKey, activeTab, isReady]);

  useEffect(() => {
    if (isReady && activeTab === 'logs') {
      fetchLogs();
    }
  }, [pagination.page, pagination.limit, filters, activeTab, isReady]);

  const fetchApiKeys = async () => {
    try {
      const response = await apiKeyService.getAll();
      if (mountedRef.current) {
        setApiKeys(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    }
  };

  const fetchAnalytics = async (retryCount = 0) => {
    try {
      setAnalyticsLoading(true);
      const params = { period };
      if (selectedKey) params.keyId = selectedKey;

      const [statsRes, overviewRes] = await Promise.all([
        usageService.getStats(params),
        usageService.getOverview()
      ]);

      if (mountedRef.current) {
        setStats(statsRes.data.data);
        setOverview(overviewRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Retry up to 2 times with delay
      if (retryCount < 2 && mountedRef.current) {
        await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
        return fetchAnalytics(retryCount + 1);
      }
      if (mountedRef.current) {
        toast.error('Failed to load analytics data');
      }
    } finally {
      if (mountedRef.current) {
        setAnalyticsLoading(false);
      }
    }
  };

  const fetchLogs = async (retryCount = 0) => {
    try {
      setLogsLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '')
        )
      };

      const response = await usageService.getLogs(params);
      if (mountedRef.current) {
        setLogs(response.data.data.logs || []);
        setPagination(prev => ({
          ...prev,
          ...response.data.data.pagination
        }));
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      // Retry up to 2 times with delay
      if (retryCount < 2 && mountedRef.current) {
        await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
        return fetchLogs(retryCount + 1);
      }
      if (mountedRef.current) {
        toast.error('Failed to load logs');
      }
    } finally {
      if (mountedRef.current) {
        setLogsLoading(false);
      }
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    if (activeTab === 'analytics') {
      await fetchAnalytics();
    } else {
      await fetchLogs();
    }
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ keyId: '', status: '', endpoint: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const exportLogs = (format) => {
    if (logs.length === 0) {
      toast.error('No logs to export');
      return;
    }

    let content, filename, type;

    if (format === 'json') {
      content = JSON.stringify(logs, null, 2);
      filename = `api-logs-${new Date().toISOString().split('T')[0]}.json`;
      type = 'application/json';
    } else {
      const headers = ['Timestamp', 'Endpoint', 'Method', 'Status', 'Response Time', 'API Key', 'Error'];
      const rows = logs.map(log => [
        log.timestamp,
        log.endpoint,
        log.method,
        log.statusCode,
        `${log.responseTime}ms`,
        log.apiKeyId?.keyPrefix || 'N/A',
        log.errorMessage || ''
      ]);
      content = [headers, ...rows].map(row => row.join(',')).join('\n');
      filename = `api-logs-${new Date().toISOString().split('T')[0]}.csv`;
      type = 'text/csv';
    }

    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported as ${format.toUpperCase()}`);
  };

  // Chart data formatters
  const getRequestsByDateData = () => {
    if (!stats?.charts?.requestsByDate) return [];
    return stats.charts.requestsByDate
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map(item => ({
        date: format(parseISO(item.date), 'MMM dd'),
        requests: item.count
      }));
  };

  const getRequestsByEndpointData = () => {
    if (!stats?.charts?.requestsByEndpoint) return [];
    return stats.charts.requestsByEndpoint.map(item => ({
      name: item.endpoint.replace('/api/analyze/', '').replace('/api/', '') || 'other',
      value: item.count
    }));
  };

  const getRequestsByStatusData = () => {
    if (!stats?.charts?.requestsByStatus) return [];
    return stats.charts.requestsByStatus.map(item => ({
      name: item.status,
      value: item.count,
      fill: item.status === '2xx' ? '#22c55e' : item.status === '4xx' ? '#f97316' : '#ef4444'
    }));
  };

  const getStatusBadge = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
          <CheckCircle className="w-3 h-3 mr-1" />
          {statusCode}
        </span>
      );
    } else if (statusCode >= 400 && statusCode < 500) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
          <AlertCircle className="w-3 h-3 mr-1" />
          {statusCode}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
          <XCircle className="w-3 h-3 mr-1" />
          {statusCode}
        </span>
      );
    }
  };

  const getEndpointLabel = (endpoint) => {
    const labels = {
      '/api/analyze/single': 'Single',
      '/api/analyze/multi': 'Multi',
      '/api/analyze/urls': 'URLs',
      '/api/analyze/streaming': 'Stream'
    };
    return labels[endpoint] || endpoint.split('/').pop() || 'Other';
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-dark-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const StatCard = ({ title, value, icon: Icon, color = 'primary', subtitle }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-dark-900 rounded-xl p-4 border border-gray-200 dark:border-dark-800 relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-20 h-20 -mr-4 -mt-4 opacity-10">
        <Icon className={`w-full h-full text-${color}-500`} />
      </div>
      <div className="relative">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-500 dark:text-gray-400">{title}</span>
          <div className={`p-1.5 rounded-lg bg-${color}-100 dark:bg-${color}-900/30`}>
            <Icon className={`w-3 h-3 text-${color}-600 dark:text-${color}-400`} />
          </div>
        </div>
        <div className="text-2xl font-bold">{value}</div>
        {subtitle && (
          <div className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</div>
        )}
      </div>
    </motion.div>
  );

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  // Log Detail Modal
  const LogDetailModal = ({ log, onClose }) => {
    if (!log) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-dark-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Request Details</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {format(parseISO(log.timestamp), 'PPpp')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)] space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Endpoint</div>
                <div className="font-medium flex items-center">
                  <Globe className="w-4 h-4 mr-2 text-gray-400" />
                  {log.endpoint}
                </div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Method</div>
                <div className="font-medium">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${
                    log.method === 'POST' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                    log.method === 'GET' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                  }`}>
                    {log.method}
                  </span>
                </div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Status</div>
                <div className="font-medium">{getStatusBadge(log.statusCode)}</div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Response Time</div>
                <div className="font-medium flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-gray-400" />
                  {log.responseTime}ms
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">API Key</div>
              <div className="font-medium flex items-center">
                <Key className="w-4 h-4 mr-2 text-gray-400" />
                {log.apiKeyId?.name || 'Unknown'} ({log.apiKeyId?.keyPrefix}...)
              </div>
            </div>

            {log.metadata && Object.keys(log.metadata).length > 0 && (
              <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Metadata</div>
                <pre className="font-mono text-sm bg-dark-900 text-gray-300 p-3 rounded-lg overflow-x-auto">
                  {JSON.stringify(log.metadata, null, 2)}
                </pre>
              </div>
            )}

            {log.errorMessage && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <div className="text-sm text-red-600 dark:text-red-400 font-medium mb-1">Error Message</div>
                <div className="text-red-700 dark:text-red-300">{log.errorMessage}</div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Analytics & Logs</h1>
          <p className="text-gray-500 dark:text-gray-400">
            Monitor your API usage, performance metrics, and request history
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="btn-outline flex items-center space-x-2 self-start"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 dark:bg-dark-800 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${
            activeTab === 'analytics'
              ? 'bg-white dark:bg-dark-900 text-primary-600 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          <span>Analytics</span>
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${
            activeTab === 'logs'
              ? 'bg-white dark:bg-dark-900 text-primary-600 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <FileText className="w-4 h-4" />
          <span>Request Logs</span>
        </button>
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Period Filter */}
          <div className="card">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium">Period:</span>
              </div>
              <div className="flex gap-2">
                {periods.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => setPeriod(p.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      period === p.value
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-dark-800 hover:bg-gray-200 dark:hover:bg-dark-700'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {apiKeys.length > 0 && (
                <>
                  <div className="w-px h-8 bg-gray-200 dark:bg-dark-700 hidden sm:block" />
                  <div className="flex items-center space-x-2">
                    <Filter className="w-5 h-5 text-gray-400" />
                    <select
                      value={selectedKey}
                      onChange={(e) => setSelectedKey(e.target.value)}
                      className="input py-2 text-sm min-w-[180px]"
                    >
                      <option value="">All API Keys</option>
                      {apiKeys.map((key) => (
                        <option key={key._id} value={key._id}>
                          {key.name} ({key.keyPrefix}...)
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          {analyticsLoading ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <div className="text-center">
                <div className="spinner mx-auto mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400">Loading analytics...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  title="Total Requests"
                  value={stats?.summary?.totalRequests?.toLocaleString() || '0'}
                  icon={Activity}
                  color="primary"
                />
                <StatCard
                  title="Success Rate"
                  value={stats?.summary?.successRate || '0%'}
                  icon={CheckCircle}
                  color="green"
                />
                <StatCard
                  title="Avg Response"
                  value={`${stats?.summary?.avgResponseTime || 0}ms`}
                  icon={Clock}
                  color="blue"
                />
                <StatCard
                  title="Failed"
                  value={stats?.summary?.failedRequests?.toLocaleString() || '0'}
                  icon={XCircle}
                  color="red"
                />
              </div>

              {/* Overview Stats */}
              {overview && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="card bg-gradient-to-br from-primary-500/10 to-orange-500/10 border-primary-200 dark:border-primary-800"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-primary-500 rounded-xl">
                        <Zap className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Today</div>
                        <div className="text-2xl font-bold">{overview.stats?.todayRequests?.toLocaleString() || 0}</div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="card bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-blue-200 dark:border-blue-800"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-blue-500 rounded-xl">
                        <BarChart3 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">This Month</div>
                        <div className="text-2xl font-bold">{overview.stats?.monthRequests?.toLocaleString() || 0}</div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="card bg-gradient-to-br from-green-500/10 to-teal-500/10 border-green-200 dark:border-green-800"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-green-500 rounded-xl">
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">All Time</div>
                        <div className="text-2xl font-bold">{overview.stats?.totalRequests?.toLocaleString() || 0}</div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              )}

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Requests Over Time */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card"
                >
                  <h3 className="text-lg font-semibold mb-4">Requests Over Time</h3>
                  {getRequestsByDateData().length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={getRequestsByDateData()}>
                        <defs>
                          <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="requests"
                          stroke="#ef4444"
                          strokeWidth={2}
                          fill="url(#colorRequests)"
                          name="Requests"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[280px] text-gray-500 dark:text-gray-400">
                      No data available for this period
                    </div>
                  )}
                </motion.div>

                {/* Requests by Endpoint */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card"
                >
                  <h3 className="text-lg font-semibold mb-4">Requests by Endpoint</h3>
                  {getRequestsByEndpointData().length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={getRequestsByEndpointData()}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={90}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {getRequestsByEndpointData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[280px] text-gray-500 dark:text-gray-400">
                      No data available for this period
                    </div>
                  )}
                </motion.div>
              </div>
            </>
          )}
        </motion.div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Filters & Export */}
          <div className="card">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 text-gray-700 dark:text-gray-300"
              >
                <Filter className="w-5 h-5" />
                <span className="font-medium">Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-xs rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              <div className="flex items-center space-x-2">
                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Clear all
                  </button>
                )}
                <div className="relative">
                  <button
                    onClick={() => document.getElementById('export-menu').classList.toggle('hidden')}
                    className="btn-outline flex items-center space-x-2 py-2"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export</span>
                  </button>
                  <div
                    id="export-menu"
                    className="hidden absolute right-0 mt-2 w-40 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700 z-20"
                  >
                    <button
                      onClick={() => {
                        exportLogs('json');
                        document.getElementById('export-menu').classList.add('hidden');
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-dark-700 rounded-t-lg text-sm"
                    >
                      Export as JSON
                    </button>
                    <button
                      onClick={() => {
                        exportLogs('csv');
                        document.getElementById('export-menu').classList.add('hidden');
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-dark-700 rounded-b-lg text-sm"
                    >
                      Export as CSV
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 mt-4 border-t border-gray-200 dark:border-dark-700">
                    <div>
                      <label className="block text-sm font-medium mb-2">API Key</label>
                      <select
                        value={filters.keyId}
                        onChange={(e) => handleFilterChange('keyId', e.target.value)}
                        className="input"
                      >
                        <option value="">All API Keys</option>
                        {apiKeys.map((key) => (
                          <option key={key._id} value={key._id}>
                            {key.name} ({key.keyPrefix}...)
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Status Code</label>
                      <select
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className="input"
                      >
                        {statusOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Endpoint</label>
                      <select
                        value={filters.endpoint}
                        onChange={(e) => handleFilterChange('endpoint', e.target.value)}
                        className="input"
                      >
                        {endpoints.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Logs Table */}
          <div className="card overflow-hidden">
            {logsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-medium mb-2">No logs found</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {activeFiltersCount > 0
                    ? 'Try adjusting your filters'
                    : 'Logs will appear here once you start making API requests'}
                </p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-dark-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Timestamp
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Endpoint
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Response
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          API Key
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                      {logs.map((log) => (
                        <tr
                          key={log._id}
                          className="hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors cursor-pointer"
                          onClick={() => setSelectedLog(log)}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {format(parseISO(log.timestamp), 'MMM dd, HH:mm:ss')}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className="px-2 py-1 bg-gray-100 dark:bg-dark-700 rounded text-xs font-medium">
                              {getEndpointLabel(log.endpoint)}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {getStatusBadge(log.statusCode)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`font-mono ${
                              log.responseTime < 500 ? 'text-green-600' :
                              log.responseTime < 1000 ? 'text-orange-600' : 'text-red-600'
                            }`}>
                              {log.responseTime}ms
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {log.apiKeyId?.keyPrefix ? `${log.apiKeyId.keyPrefix}...` : 'N/A'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <Eye className="w-4 h-4 text-gray-400" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden space-y-3 p-4">
                  {logs.map((log) => (
                    <motion.div
                      key={log._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gray-50 dark:bg-dark-800 rounded-lg p-4"
                      onClick={() => setSelectedLog(log)}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {format(parseISO(log.timestamp), 'MMM dd, HH:mm')}
                        </span>
                        {getStatusBadge(log.statusCode)}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="px-2 py-1 bg-gray-200 dark:bg-dark-700 rounded text-xs font-medium">
                          {getEndpointLabel(log.endpoint)}
                        </span>
                        <span className="text-sm font-mono text-gray-600 dark:text-gray-400">
                          {log.responseTime}ms
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="card">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </span>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                      let pageNum;
                      if (pagination.pages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.pages - 2) {
                        pageNum = pagination.pages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                          className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                            pageNum === pagination.page
                              ? 'bg-primary-600 text-white'
                              : 'hover:bg-gray-100 dark:hover:bg-dark-800'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Log Detail Modal */}
      <AnimatePresence>
        {selectedLog && (
          <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnalyticsLogsPage;
